import { round2 } from "../../utils/money";
// frontend/src/components/EditSalesOrder.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import {
  Package, Warehouse, Building, Calendar, Hash, DollarSign,
  ClipboardList, Percent, Trash2, User, Truck,
  CreditCard, FileText, Calculator, Save, X, Clock, AlertCircle,
  Loader2, RefreshCw, AlertTriangle,
  ArrowUp, ArrowDown, Download, Wallet,
  CreditCard as CreditCardIcon, Smartphone, Plus, Layers, Box,
  CheckCircle, FilePlus, Users
} from "lucide-react";
import * as XLSX from "xlsx";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./SalesOrder.css";

const url = process.env.REACT_APP_BACKEND_URL;

const EditSalesOrder = () => {
  const { id } = useParams();
  const { currentUser, userType, company } = useAuth();
  const { formatCurrency, getCurrencySymbol } = useCurrency();
  const navigate = useNavigate();

  // State
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [salesOrder, setSalesOrder] = useState(null);

  // Dropdown data
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [transactionTypes, setTransactionTypes] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [productStock, setProductStock] = useState({});
  const [productPrices, setProductPrices] = useState({});
  const [discounts, setDiscounts] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [paymentMethodsData, setPaymentMethodsData] = useState({ methods: [] });
  const [postedPayments, setPostedPayments] = useState([]);

  // Loading states
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Order status options
  const orderStatusOptions = [
    { value: "draft", label: "Draft", color: "#fa8c16", bg: "#fff7e6" },
    { value: "confirmed", label: "Confirmed", color: "#1890ff", bg: "#e6f7ff" },
    { value: "processing", label: "Processing", color: "#722ed1", bg: "#f9f0ff" },
    { value: "shipped", label: "Shipped", color: "#13c2c2", bg: "#e6fffb" },
    { value: "delivered", label: "Delivered", color: "#52c41a", bg: "#f6ffed" },
    { value: "cancelled", label: "Cancelled", color: "#ff4d4f", bg: "#fff2f0" },
    { value: "partially_delivered", label: "Partially Delivered", color: "#fa8c16", bg: "#fff7e6" }
  ];

  // Form state
  const [formData, setFormData] = useState({
    warehouse_id: "",
    customer_id: "",
    transaction_type_id: "",
    order_no: "",
    order_date: new Date().toISOString().split('T')[0],
    expected_delivery_date: "",
    shipping_cost: "0",
    other_charges: "0",
    requires_approval: false,
    cancellation_reason: "",

    discount_id: "",
    tax_id: "",
    manual_discount: "",
  });

  // Hidden fields
  const [orderStatus, setOrderStatus] = useState("draft");
  const [paymentStatus, setPaymentStatus] = useState("pending");

  // Items array
  const [items, setItems] = useState([]);

  // Focus states
  const [focusedFields, setFocusedFields] = useState({});
  const [errors, setErrors] = useState({});

  // Totals
  const [totals, setTotals] = useState({
    subTotal: 0,
    selectedDiscountAmount: 0,
    manualDiscountAmount: 0,
    totalDiscount: 0,
    selectedTaxAmount: 0,
    totalTax: 0,
    shippingCost: 0,
    otherCharges: 0,
    grandTotal: 0,
  });

  // Delivery stats
  const [deliveryStats, setDeliveryStats] = useState({
    totalItems: 0,
    deliveredItems: 0,
    pendingItems: 0,
    deliveryPercentage: 0
  });

  const isSystemAdmin = userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');

  // Get company ID
  const getCompanyId = () => {
    if (salesOrder) {
      return salesOrder.company_id?._id || salesOrder.company_id;
    }
    if (userType === 'company' && company) {
      return company._id || company;
    }
    return null;
  };

  // Fetch product price
  const fetchProductPrice = async (productId) => {
    if (!productId) return 0;

    if (productPrices[productId] !== undefined) {
      return productPrices[productId];
    }

    try {
      const companyId = getCompanyId();

      const response = await axios.get(`${url}/api/products/${productId}`, {
        params: { company_id: companyId }
      });

      if (response.data.success) {
        const price = response.data.data.sale_price || response.data.data.unit_price || 0;
        setProductPrices(prev => ({ ...prev, [productId]: price }));
        return price;
      }
      return 0;
    } catch (error) {
      return 0;
    }
  };

  // Fetch products for specific warehouse with stock info
  const fetchProductsForWarehouse = async (warehouseId) => {
    if (!warehouseId) {
      setFilteredProducts(products);
      setProductStock({});
      return;
    }

    try {
      setLoadingProducts(true);
      const companyId = getCompanyId();

      const response = await axios.get(`${url}/api/inventorystock/warehouse/${warehouseId}`, {
        params: {
          company_id: companyId,
          status: 'active'
        }
      });

      if (response.data.success) {
        const stockMap = {};
        const warehouseProducts = [];

        for (const stock of response.data.data) {
          let productId;
          let productName;
          let productCode;
          let salePrice = 0;

          if (stock.product_id && typeof stock.product_id === 'object') {
            productId = stock.product_id._id;
            productName = stock.product_id.product_name;
            productCode = stock.product_id.product_code;
            salePrice = stock.product_id.sale_price || stock.product_id.unit_price || 0;
          } else {
            productId = stock.product_id;
            const foundProduct = products.find(p => p._id === productId);
            productName = foundProduct?.product_name || 'Unknown';
            productCode = foundProduct?.product_code || 'N/A';
            salePrice = foundProduct?.sale_price || foundProduct?.unit_price || 0;
          }

          stockMap[productId] = {
            current_stock: stock.quantity || 0,
            available_stock: stock.available_qty || 0,
            reserved_stock: stock.reserved_qty || 0,
            avg_cost: stock.avg_cost || 0
          };

          warehouseProducts.push({
            _id: productId,
            product_id: productId,
            product_code: productCode,
            product_name: productName,
            current_stock: stock.quantity || 0,
            available_stock: stock.available_qty || 0,
            reserved_stock: stock.reserved_qty || 0,
            sale_price: salePrice
          });

          if (salePrice > 0) {
            setProductPrices(prev => ({ ...prev, [productId]: salePrice }));
          }
        }

        setProductStock(stockMap);
        setFilteredProducts(warehouseProducts);
      } else {
        setFilteredProducts(products);
        setProductStock({});
      }
    } catch (error) {
      setFilteredProducts(products);
      setProductStock({});
    } finally {
      setLoadingProducts(false);
    }
  };

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch order details
        const orderRes = await axios.get(`${url}/api/salesorders/${id}`, {
          timeout: 10000
        });

        const order = orderRes.data.data;
        setSalesOrder(order);
        const companyId = order.company_id?._id || order.company_id;

        // Fetch dropdown data first
        await fetchAllDropdownData(companyId);

        // Set form data
        setFormData({
          warehouse_id: order.warehouse_id?._id || order.warehouse_id || "",
          customer_id: order.customer_id?._id || order.customer_id || "",
          transaction_type_id: order.transaction_type_id?._id || order.transaction_type_id || "",
          order_no: order.order_no || "",
          order_date: order.order_date
            ? new Date(order.order_date).toISOString().split('T')[0]
            : new Date().toISOString().split('T')[0],
          expected_delivery_date: order.expected_delivery_date
            ? new Date(order.expected_delivery_date).toISOString().split('T')[0]
            : "",
          shipping_cost: order.shipping_cost?.toString() || "0",
          other_charges: order.other_charges?.toString() || "0",
          requires_approval: order.requires_approval || false,
          cancellation_reason: order.cancellation_reason || "",
          discount_id:
            order.discount_id?._id ||
            order.discount_id ||
            "",
          tax_id:
            order.tax_id?._id ||
            order.tax_id ||
            "",
          manual_discount:
            Number(order.manual_discount || 0) > 0
              ? String(order.manual_discount)
              : "",
        });

        // Set status fields
        setOrderStatus(order.status || "draft");
        setPaymentStatus(order.payment_status || "pending");
        setPostedPayments(
          Array.isArray(order.payment_details)
            ? order.payment_details.map((payment) => ({
              method: payment.method || "",
              account_id:
                payment.account_id?._id ||
                payment.account_id ||
                payment.account?._id ||
                payment.account ||
                "",
              account_name:
                payment.account_name ||
                payment.account_id?.account_name ||
                payment.account?.account_name ||
                "",
              amount: Number(payment.amount || 0),
              reference:
                payment.reference ||
                payment.reference_no ||
                "",
            }))
            : [],
        );

        // Set items data
        if (order.items && order.items.length > 0) {
          const formattedItems = order.items.map((item) => ({
            product_id: item.product_id?._id || item.product_id || "",
            quantity: item.quantity?.toString() || "",
            unit_price: item.unit_price?.toString() || "",
            discount_amount:
              Number(item.discount_amount || item.discount_flat || 0),
            tax_amount:
              Number(item.tax_amount || 0),
            tax_inclusive:
              Boolean(item.tax_inclusive),
            total_price: item.total_price?.toString() || "0",
            notes: item.notes || "",
            delivered_quantity: item.delivered_quantity || 0,
            returned_quantity: item.returned_quantity || 0,
            status: item.status || "pending",
            original_item: item,
          }));
          setItems(formattedItems);

          // Calculate delivery stats
          const totalItems = formattedItems.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0);
          const deliveredItems = formattedItems.reduce((sum, item) => sum + (item.delivered_quantity || 0), 0);
          setDeliveryStats({
            totalItems,
            deliveredItems,
            pendingItems: totalItems - deliveredItems,
            deliveryPercentage: totalItems > 0 ? (deliveredItems / totalItems) * 100 : 0
          });
        }

        // Fetch products for warehouse if warehouse is selected
        if (order.warehouse_id?._id || order.warehouse_id) {
          const warehouseId = order.warehouse_id?._id || order.warehouse_id;
          await fetchProductsForWarehouse(warehouseId);
        }

      } catch (error) {
        setError(error.message);

        let errorMsg = "Failed to load sales order";
        if (error.response) {
          errorMsg = error.response.data?.message || errorMsg;
          if (error.response.status === 404) {
            errorMsg = "Sales order not found";
          }
        }

        toast.error(errorMsg);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser && id) {
      fetchData();
    }
  }, [currentUser, id, navigate]);

  // Fetch all dropdown data
  const fetchAllDropdownData = async (companyId) => {
    if (!companyId) {
      return;
    }

    try {
      const params = { company_id: companyId, status: 'active' };

      // Fetch warehouses
      try {
        const whRes = await axios.get(`${url}/api/warehouse/dropdown`, { params });
        if (whRes.data?.success) {
          setWarehouses(whRes.data.data || []);
        }
      } catch (err) {
        setWarehouses([]);
      }

      // Fetch customers
      try {
        const supRes = await axios.get(`${url}/api/customer/dropdown`, { params });
        if (supRes.data?.success) {
          setCustomers(supRes.data.data || []);
        }
      } catch (err) {
        setCustomers([]);
      }

      // Fetch transaction types
      try {
        const ttRes = await axios.get(`${url}/api/transactiontype/dropdown`, { params });
        if (ttRes.data?.success) {
          const allTypes = ttRes.data.data || [];

          // Filter to show ONLY sales_order types
          const orderTypes = allTypes.filter(type => {
            const typeName = (type.transaction_type || '').toLowerCase();
            return typeName === 'sales_order' ||
              (typeName.includes('order') && type.sign === '0');
          });

          setTransactionTypes(orderTypes);
        }
      } catch (err) {
        setTransactionTypes([]);
      }

      // Fetch dynamic sales discounts, taxes and approved payment accounts.
      try {
        const paymentRes = await axios.get(
          `${url}/api/salesorders/payment-methods`,
          { params: { company_id: companyId } },
        );

        if (paymentRes.data?.success) {
          const options = paymentRes.data.data || {};
          setPaymentMethodsData({ methods: options.methods || [] });
          setDiscounts(options.discounts || []);
          setTaxes(options.taxes || []);
        }
      } catch (err) {
        setPaymentMethodsData({ methods: [] });
        setDiscounts([]);
        setTaxes([]);
      }

      // Fetch products
      try {
        const prodRes = await axios.get(`${url}/api/products/dropdown`, { params });
        if (prodRes.data?.success) {
          const productData = prodRes.data.data || [];
          setProducts(productData);
          setFilteredProducts(productData);

          // Cache product prices
          const priceMap = {};
          for (const product of productData) {
            if (product.sale_price || product.unit_price) {
              priceMap[product._id] = product.sale_price || product.unit_price || 0;
            }
          }
          setProductPrices(priceMap);
        }
      } catch (err) {
        setProducts([]);
        setFilteredProducts([]);
      }

    } catch (error) {
    }
  };

  // Handle warehouse change
  const handleWarehouseChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (value) {
      fetchProductsForWarehouse(value);
    } else {
      setFilteredProducts(products);
      setProductStock({});
    }
  };

  // Calculate totals
  useEffect(() => {
    calculateTotals();
  }, [
    items,
    formData.shipping_cost,
    formData.other_charges,
    formData.discount_id,
    formData.tax_id,
    formData.manual_discount,
    discounts,
    taxes,
  ]);

  const getSelectedDiscount = () =>
    discounts.find(
      (discount) =>
        String(discount._id) === String(formData.discount_id),
    );

  const getSelectedTax = () =>
    taxes.find(
      (tax) =>
        String(tax._id) === String(formData.tax_id),
    );

  const calculateOrderTotals = () => {
    const subTotal = items.reduce(
      (sum, item) =>
        sum +
        (Number(item.quantity) || 0) *
        (Number(item.unit_price) || 0),
      0,
    );

    const selectedDiscount = getSelectedDiscount();

    let selectedDiscountAmount = 0;

    if (selectedDiscount?.discount_type === "percentage") {
      selectedDiscountAmount =
        subTotal * (Number(selectedDiscount.value || 0) / 100);
    } else if (selectedDiscount?.discount_type === "fixed") {
      selectedDiscountAmount = Number(selectedDiscount.value || 0);
    }

    selectedDiscountAmount = Math.min(
      Math.max(selectedDiscountAmount, 0),
      subTotal,
    );

    const manualDiscountAmount = Math.min(
      Math.max(Number(formData.manual_discount || 0), 0),
      Math.max(subTotal - selectedDiscountAmount, 0),
    );

    const totalDiscount =
      selectedDiscountAmount + manualDiscountAmount;

    const taxableAmount = Math.max(
      subTotal - totalDiscount,
      0,
    );

    const selectedTax = getSelectedTax();

    let selectedTaxAmount = 0;
    let selectedTaxAddedToTotal = 0;

    if (selectedTax?.tax_type === "percentage") {
      const rate = Number(selectedTax.value || 0);

      if (selectedTax.price_inclusive) {
        selectedTaxAmount =
          rate > 0
            ? taxableAmount -
            taxableAmount / (1 + rate / 100)
            : 0;
      } else {
        selectedTaxAmount = taxableAmount * (rate / 100);
        selectedTaxAddedToTotal = selectedTaxAmount;
      }
    } else if (selectedTax?.tax_type === "fixed") {
      selectedTaxAmount = Math.max(
        Number(selectedTax.value || 0),
        0,
      );

      if (!selectedTax.price_inclusive) {
        selectedTaxAddedToTotal = selectedTaxAmount;
      }
    }

    // manual_tax removed – only the selected tax from tax_id is used
    const totalTax = selectedTaxAmount;

    const shippingCost = Math.max(
      Number(formData.shipping_cost || 0),
      0,
    );
    const otherCharges = Math.max(
      Number(formData.other_charges || 0),
      0,
    );

    const grandTotal =
      taxableAmount +
      selectedTaxAddedToTotal +
      shippingCost +
      otherCharges;

    return {
      subTotal,
      selectedDiscountAmount,
      manualDiscountAmount,
      totalDiscount,
      taxableAmount,
      selectedTaxAmount,
      totalTax,
      shippingCost,
      otherCharges,
      grandTotal,
    };
  };

  const calculateItem = (item, index = 0) => {
    const orderTotals = calculateOrderTotals();
    const gross =
      (Number(item.quantity) || 0) *
      (Number(item.unit_price) || 0);

    const allGross = items.map(
      (row) =>
        (Number(row.quantity) || 0) *
        (Number(row.unit_price) || 0),
    );

    const ratio =
      orderTotals.subTotal > 0
        ? gross / orderTotals.subTotal
        : 0;

    const previousGross = allGross
      .slice(0, index)
      .reduce((sum, value) => sum + value, 0);

    const previousRatio =
      orderTotals.subTotal > 0
        ? previousGross / orderTotals.subTotal
        : 0;

    const isLast = index === items.length - 1;

    const discountAmount = isLast
      ? orderTotals.totalDiscount -
      Number(
        (
          orderTotals.totalDiscount * previousRatio
        ).toFixed(2),
      )
      : Number(
        round2(orderTotals.totalDiscount * ratio),
      );

    const taxAmount = isLast
      ? orderTotals.totalTax -
      Number(
        round2(orderTotals.totalTax * previousRatio),
      )
      : Number(
        round2(orderTotals.totalTax * ratio),
      );

    const total =
      gross - discountAmount + taxAmount;

    return {
      gross,
      discountAmount,
      taxAmount,
      total,
    };
  };

  const calculateTotals = () => {
    setTotals(calculateOrderTotals());
  };

  // Calculate item total
  const calculateItemTotal = (item, index = 0) =>
    calculateItem(item, index).total;

  // Handle product selection with price
  const handleProductSelect = async (index, productId) => {
    if (!productId) return;

    try {
      let salePrice = productPrices[productId];

      if (salePrice === undefined) {
        salePrice = await fetchProductPrice(productId);
      }

      setItems(prev => {
        const updated = [...prev];
        updated[index].product_id = productId;
        updated[index].unit_price = salePrice.toString();

        const qty = Number(updated[index].quantity) || 0;
        updated[index].total_price = (
          qty * Number(salePrice || 0)
        ).toFixed(2);

        return updated;
      });

    } catch (error) {
      toast.error("Failed to fetch product price");
    }
  };

  // ========== ITEM HANDLERS ==========
  const addItemRow = () => {
    setItems(prev => [...prev, {
      product_id: "",
      quantity: "",
      unit_price: "",
      discount_amount: 0,
      tax_amount: 0,
      tax_inclusive: false,
      total_price: "0",
      notes: "",
      delivered_quantity: 0,
      returned_quantity: 0,
      status: "pending"
    }]);
  };

  const removeItemRow = (index) => {
    if (items.length > 1) {
      const item = items[index];
      if (item.delivered_quantity > 0) {
        toast.error("Cannot remove item that has been partially delivered");
        return;
      }
      setItems(prev => prev.filter((_, i) => i !== index));
    } else {
      toast.warning("At least one item is required");
    }
  };

  // ========== FORM HANDLERS ==========
  const handleFocus = (field) =>
    setFocusedFields((prev) => ({ ...prev, [field]: true }));

  const handleBlur = (field) => {
    const value = formData[field];
    setFocusedFields((prev) => ({
      ...prev,
      [field]: (typeof value === 'string' ? value.trim() : value) ? true : false
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'warehouse_id') {
      handleWarehouseChange(e);
    } else if (
      [
        "shipping_cost",
        "other_charges",
        "manual_discount",
      ].includes(name)
    ) {
      if (value === '' || /^\d*\.?\d*$/.test(value)) {
        setFormData(prev => ({ ...prev, [name]: value }));
      }
    } else if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleStatusChange = (e) => {
    const newStatus = e.target.value;

    if (newStatus === 'cancelled' && orderStatus !== 'cancelled') {
      if (deliveryStats.deliveredItems > 0) {
        toast.error("Cannot cancel order with delivered items");
        return;
      }
    }

    setOrderStatus(newStatus);
  };

  const handleItemChange = (index, field, value) => {
    setItems((previous) => {
      const updated = previous.map((item) => ({ ...item }));
      const row = updated[index];

      if (!row) return previous;

      if (["quantity", "unit_price"].includes(field)) {
        if (value === "" || /^\d*\.?\d*$/.test(value)) {
          row[field] = value;
        }
      } else {
        row[field] = value;
      }


      row.total_price = (
        (Number(row.quantity) || 0) *
        (Number(row.unit_price) || 0)
      ).toFixed(2);
      return updated;
    });

    if (errors[`${field}_${index}`]) {
      setErrors((previous) => ({
        ...previous,
        [`${field}_${index}`]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.warehouse_id) {
      newErrors.warehouse_id = "Warehouse is required";
    }
    if (!formData.customer_id) {
      newErrors.customer_id = "Customer is required";
    }
    if (!formData.transaction_type_id) {
      newErrors.transaction_type_id = "Transaction type is required";
    }

    const validItems = items.filter(p =>
      p.product_id && p.quantity && p.unit_price &&
      parseFloat(p.quantity) > 0 && parseFloat(p.unit_price) >= 0
    );

    if (validItems.length === 0) {
      newErrors.items = "At least one valid item is required";
    } else {
      items.forEach((item, index) => {
        if (item.product_id && (!item.quantity || parseFloat(item.quantity) <= 0)) {
          newErrors[`quantity_${index}`] = "Quantity must be greater than 0";
        }
        if (item.product_id && (!item.unit_price || parseFloat(item.unit_price) < 0)) {
          newErrors[`unit_price_${index}`] = "Unit price cannot be negative";
        }

        if (item.product_id && item.quantity) {
          const qty = parseFloat(item.quantity);
          const stockInfo = productStock[item.product_id];

          const originalItem = item.original_item;
          const originalQty = originalItem ? originalItem.quantity : 0;
          const additionalQty = Math.max(0, qty - originalQty);

          if (additionalQty > 0 && stockInfo && stockInfo.available_stock < additionalQty) {
            newErrors[`stock_${index}`] = `Insufficient available stock. Available: ${stockInfo.available_stock}`;
          }
        }
      });
    }

    if (
      Number(formData.manual_discount || 0) >
      calculateOrderTotals().subTotal -
      calculateOrderTotals().selectedDiscountAmount
    ) {
      newErrors.manual_discount =
        "Manual discount cannot exceed the remaining subtotal";
    }

    if (orderStatus === 'cancelled' && !formData.cancellation_reason) {
      newErrors.cancellation_reason = "Cancellation reason is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ========== TEMPLATE DOWNLOAD ==========
  const downloadTemplate = () => {
    const templateData = [
      {
        "Product Code": "PROD001",
        "Product Name": "Sample Product",
        "Warehouse Code": "WH001",
        "Customer Name": "Sample Customer",
        "Quantity": 5,
        "Unit Price": 25.50,
        "Discount %": 0,
        "Discount Flat": 0,
        "Tax Rate %": 10,
        "Order Date": new Date().toISOString().split('T')[0],
        "Expected Delivery": new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sales Orders");

    const wscols = [
      { wch: 15 },
      { wch: 25 },
      { wch: 15 },
      { wch: 20 },
      { wch: 10 },
      { wch: 12 },
      { wch: 10 },
      { wch: 12 },
      { wch: 10 },
      { wch: 15 },
      { wch: 15 }
    ];
    worksheet["!cols"] = wscols;

    XLSX.writeFile(workbook, `Sales-Order-Template.xlsx`);
    toast.info("Template downloaded successfully!");
  };

  // ========== CONVERT TO Invoice ==========
  const handleConvertToInvoice = async () => {
    if (!window.confirm("Convert this order to Invoice? This will create sales transactions and update inventory.")) {
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        items_to_deliver: items.map(item => ({
          product_id: item.product_id,
          quantity: parseFloat(item.quantity) - (item.delivered_quantity || 0),
          unit_price: parseFloat(item.unit_price),
          discount_id: item.discount_id || null,
          tax_id: item.tax_id || null,
        })).filter(item => item.quantity > 0),
      };

      const res = await axios.post(`${url}/api/salesorders/${id}/convert`, payload);

      if (res.data.success) {
        toast.success("Order converted to Invoice successfully!");
        setTimeout(() => {
          navigate(`/editsalestransaction/${res.data.data.transactions[0]._id}`);
        }, 1500);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to convert order");
    } finally {
      setSubmitting(false);
    }
  };

  // ========== CANCEL HANDLER ==========
  const handleCancel = () => {
    navigate("/ViewSalesOrder");
  };

  // ========== SUBMIT HANDLER ==========
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix form errors");
      return;
    }

    if (orderStatus === 'cancelled' && salesOrder?.status !== 'cancelled') {
      if (!window.confirm("Are you sure you want to cancel this order? This will release any reserved stock.")) {
        return;
      }
    }

    setSubmitting(true);
    try {
      // Filter valid items
      const validItems = items.filter(p =>
        p.product_id && p.quantity && p.unit_price &&
        parseFloat(p.quantity) > 0 && parseFloat(p.unit_price) >= 0
      ).map(item => {
        const { original_item, ...itemData } = item;
        return {
          ...itemData,
          quantity: parseFloat(itemData.quantity),
          unit_price: parseFloat(itemData.unit_price),
          discount_id:
            itemData.discount_id || formData.discount_id || null,
          tax_id:
            itemData.tax_id || formData.tax_id || null,
          discount_amount:
            calculateItem(itemData, items.indexOf(item)).discountAmount,
          tax_amount:
            calculateItem(itemData, items.indexOf(item)).taxAmount,
          total_price:
            calculateItem(itemData, items.indexOf(item)).total,
        };
      });

      const payload = {
        warehouse_id: formData.warehouse_id,
        customer_id: formData.customer_id,
        transaction_type_id: formData.transaction_type_id,
        order_no: formData.order_no,
        order_date: formData.order_date,
        expected_delivery_date: formData.expected_delivery_date || null,
        status: orderStatus,
        payment_status: paymentStatus,
        shipping_cost: parseFloat(formData.shipping_cost) || 0,
        other_charges: parseFloat(formData.other_charges) || 0,
        discount_id: formData.discount_id || null,
        tax_id: formData.tax_id || null,
        manual_discount: parseFloat(formData.manual_discount) || 0,
        requires_approval: formData.requires_approval,
        cancellation_reason: formData.cancellation_reason,

        items: validItems,
      };
      const res = await axios.put(`${url}/api/salesorders/${id}`, payload, {
        headers: {
          "Content-Type": "application/json"
        },
      });

      if (res.data.success) {
        toast.success("Sales order updated successfully!");

        setTimeout(() => {
          navigate("/ViewSalesOrder");
        }, 1500);
      }
    } catch (error) {
      let errorMsg = "Failed to update sales order";

      if (error.response) {
        errorMsg = error.response.data?.message || errorMsg;

        if (error.response.data?.message?.includes('stock')) {
          errorMsg = error.response.data.message;
        }

        if (error.response.data?.errors) {
          const serverErrors = {};

          if (Array.isArray(error.response.data.errors)) {
            error.response.data.errors.forEach(err => {
              if (err.field) {
                serverErrors[err.field] = err.message;
              }
            });
          } else if (typeof error.response.data.errors === 'object') {
            Object.keys(error.response.data.errors).forEach(key => {
              serverErrors[key] = error.response.data.errors[key];
            });
          }

          setErrors(serverErrors);
        }
      }

      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return "Invalid date";
    }
  };

  // Get order status badge
  const getOrderStatusBadge = (status) => {
    const statusMap = {
      draft: { text: 'Draft', color: '#fa8c16', bg: '#fff7e6' },
      confirmed: { text: 'Confirmed', color: '#1890ff', bg: '#e6f7ff' },
      processing: { text: 'Processing', color: '#722ed1', bg: '#f9f0ff' },
      shipped: { text: 'Shipped', color: '#13c2c2', bg: '#e6fffb' },
      delivered: { text: 'Delivered', color: '#52c41a', bg: '#f6ffed' },
      cancelled: { text: 'Cancelled', color: '#ff4d4f', bg: '#fff2f0' },
      partially_delivered: { text: 'Partially Delivered', color: '#fa8c16', bg: '#fff7e6' }
    };
    return statusMap[status] || statusMap.draft;
  };

  const getProductOptions = (currentIndex) => {
    return filteredProducts
      .filter(product => {
        if (
          items[currentIndex]?.product_id === product._id
        ) {
          return true;
        }

        const alreadySelected = items.some(
          (item, index) =>
            index !== currentIndex &&
            String(item.product_id) === String(product._id)
        );

        return !alreadySelected;
      })
      .map(product => ({
        value: product._id,
        label:
          `${product.product_name} (${product.product_code}) | Stock: ${productStock[product._id]?.available_stock || 0
          } | Price: ${formatCurrency(
            productPrices[product._id] ||
            product.sale_price ||
            0
          )}`
      }));
  };

  if (loading) {
    return (
      <div className="form-wrapper">
        <div className="order-form-container">
          <div className="loading-container">
            <Loader2 className="animate-spin" size={32} />
            <p>Loading sales order data...</p>
            <p style={{ fontSize: '12px', color: '#999' }}>Please wait...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !salesOrder) {
    return (
      <div className="form-wrapper">
        <div className="order-form-container">
          <div className="error-container">
            <AlertCircle size={48} color="#dc3545" />
            <h2>Order Not Found</h2>
            <p>{error || "Sales order not found"}</p>
            <button
              className="cancel-btn"
              onClick={handleCancel}
              style={{ marginTop: '20px' }}
            >
              Back to List
            </button>
          </div>
        </div>
      </div>
    );
  }

  const orderStatusBadge = getOrderStatusBadge(orderStatus);

  return (
    <div className="form-wrapper">
      <div className="order-form-container">
        <button
          className="close-btn"
          onClick={handleCancel}
          title="Go Back"
          type="button"
        >
          <X size={20} />
        </button>

        <div className="form-header">
          <ClipboardList size={24} />
          <h2 className="form-title">
            Edit Sales Order
          </h2>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <p className="form-subtitle">
              Update order details for {salesOrder.reference_id}
            </p>
          </div>
        </div>

        {/* Order Info Banner */}
        <div style={{
          background: '#f0f9ff',
          border: '1px solid #bae7ff',
          borderRadius: '8px',
          padding: '16px 20px',
          marginBottom: '20px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Hash size={14} color="#1890ff" />
            <span style={{ fontSize: '13px' }}>
              <strong>Reference:</strong> {salesOrder.reference_id || "N/A"}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FileText size={14} color="#1890ff" />
            <span style={{ fontSize: '13px' }}>
              <strong>Order No:</strong> {salesOrder.order_no || "N/A"}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Building size={14} color="#1890ff" />
            <span style={{ fontSize: '13px' }}>
              <strong>Company:</strong> {salesOrder.company_id?.company_name || salesOrder.company_id}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <User size={14} color="#1890ff" />
            <span style={{ fontSize: '13px' }}>
              <strong>Created:</strong> {formatDate(salesOrder.created_at)}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Clock size={14} color="#1890ff" />
            <span style={{ fontSize: '13px' }}>
              <strong>Order Date:</strong> {formatDate(salesOrder.order_date)}
            </span>
          </div>
          {salesOrder.sales_transactions && salesOrder.sales_transactions.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', gridColumn: '1 / -1' }}>
              <FilePlus size={14} color="#52c41a" />
              <span style={{ fontSize: '13px' }}>
                <strong>Invoices Created:</strong> {salesOrder.sales_transactions.length}
              </span>
            </div>
          )}
        </div>

        {/* Delivery Progress Bar */}
        <div style={{
          background: '#fafafa',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '20px',
          border: '1px solid #f0f0f0'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontWeight: 500 }}>Delivery Progress</span>
            <span style={{ color: '#1890ff', fontWeight: 600 }}>
              {deliveryStats.deliveredItems} / {deliveryStats.totalItems} items
            </span>
          </div>
          <div style={{
            height: '8px',
            background: '#f0f0f0',
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${deliveryStats.deliveryPercentage}%`,
              height: '100%',
              background: deliveryStats.deliveryPercentage === 100 ? '#52c41a' : '#1890ff',
              transition: 'width 0.3s ease'
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '12px', color: '#666' }}>
            <span>Pending: {deliveryStats.pendingItems}</span>
            <span>{deliveryStats.deliveryPercentage.toFixed(1)}% Complete</span>
          </div>
        </div>

        <form className="sales-form" onSubmit={handleSubmit}>
          {/* Status Selection */}
          <div style={{
            marginBottom: '20px',
            padding: '16px',
            background: '#fafafa',
            borderRadius: '8px',
            border: '1px solid #f0f0f0'
          }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>Order Status</label>
            <select
              value={orderStatus}
              onChange={handleStatusChange}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '4px',
                border: '1px solid #ddd',
                fontWeight: 500,
                color: orderStatusBadge.color,
                background: orderStatusBadge.bg
              }}
              disabled={orderStatus === 'delivered' || orderStatus === 'cancelled'}
            >
              {orderStatusOptions.map(option => (
                <option key={option.value} value={option.value} style={{ color: option.color }}>
                  {option.label}
                </option>
              ))}
            </select>
            {orderStatus === 'cancelled' && (
              <div style={{ marginTop: '10px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 500 }}>Cancellation Reason *</label>
                <input
                  type="text"
                  name="cancellation_reason"
                  value={formData.cancellation_reason}
                  onChange={handleChange}
                  placeholder="Reason for cancellation"
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                  required={orderStatus === 'cancelled'}
                />
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
            {/* Warehouse Selection */}
            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Warehouse *</label>
              <Select
                name="warehouse_id"

                value={
                  warehouses.find(
                    w => w._id === formData.warehouse_id
                  )
                    ?
                    {
                      value: formData.warehouse_id,
                      label:
                        `${warehouses.find(
                          w => w._id === formData.warehouse_id
                        ).warehouse_code
                        } - ${warehouses.find(
                          w => w._id === formData.warehouse_id
                        ).warehouse_name
                        }`
                    }
                    :
                    null
                }

                options={
                  warehouses.map(warehouse => ({
                    value: warehouse._id,
                    label:
                      `${warehouse.warehouse_code} - ${warehouse.warehouse_name}`
                  }))
                }

                placeholder={
                  warehouses.length === 0
                    ? "No warehouses available"
                    : "Search Warehouse..."
                }

                isSearchable={true}

                isDisabled={
                  warehouses.length === 0 ||
                  orderStatus === "delivered" ||
                  orderStatus === "cancelled"
                }

                onChange={(selected) => {
                  handleChange({
                    target: {
                      name: "warehouse_id",
                      value: selected ? selected.value : ""
                    }
                  });
                }}

                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: "38px",
                    border: "1px solid #ddd",
                    borderRadius: "4px"
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999
                  })
                }}

                required
              />
            </div>

            {/* Customer Selection */}
            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Customer *</label>
              <Select
                name="customer_id"

                value={
                  customers.find(
                    c => c._id === formData.customer_id
                  )
                    ?
                    {
                      value: formData.customer_id,
                      label:
                        `${customers.find(
                          c => c._id === formData.customer_id
                        ).customer_name
                        } (${customers.find(
                          c => c._id === formData.customer_id
                        ).customer_code
                        })`
                    }
                    :
                    null
                }


                options={
                  customers.map(customer => ({
                    value: customer._id,
                    label:
                      `${customer.customer_name} (${customer.customer_code})`
                  }))
                }


                placeholder={
                  customers.length === 0
                    ? "No customers available"
                    : "Search Customer..."
                }


                isSearchable={true}


                isDisabled={
                  customers.length === 0 ||
                  orderStatus === "delivered" ||
                  orderStatus === "cancelled"
                }


                onChange={(selected) => {
                  handleChange({
                    target: {
                      name: "customer_id",
                      value: selected ? selected.value : ""
                    }
                  });
                }}


                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: "38px",
                    border: "1px solid #ddd",
                    borderRadius: "4px"
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999
                  })
                }}


                required
              />
            </div>

            {/* Transaction Type - Read-only */}
            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Transaction Type *</label>
              <input
                type="text"
                value={transactionTypes.find(t => t._id === formData.transaction_type_id)?.transaction_type ||
                  salesOrder?.transaction_type_id?.transaction_type || 'Sales Order'}
                disabled
                style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd', background: '#f5f5f5' }}
              />
              <small style={{ color: '#ff4d4f' }}>Transaction type cannot be changed</small>
            </div>

            {/* Order Number - Read-only */}
            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Order No</label>
              <input
                type="text"
                name="order_no"
                value={formData.order_no}
                disabled
                style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd', background: '#f5f5f5' }}
              />
              <small style={{ color: '#1890ff' }}>Order number cannot be changed</small>
            </div>

            {/* Order Date */}
            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Order Date *</label>
              <input
                type="date"
                name="order_date"
                value={formData.order_date}
                onChange={handleChange}
                required
                disabled={orderStatus === 'delivered' || orderStatus === 'cancelled'}
                style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
              />
            </div>

            {/* Expected Delivery Date */}
            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Expected Delivery</label>
              <input
                type="date"
                name="expected_delivery_date"
                value={formData.expected_delivery_date}
                onChange={handleChange}
                disabled={orderStatus === 'delivered' || orderStatus === 'cancelled'}
                style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
              />
            </div>
          </div>

          {/* Items Section */}
          <h3 className="section-title">Order Items</h3>
          <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
            Note: Changing quantities may affect stock reservation
          </p>

          {errors.items && (
            <div style={{
              marginBottom: '15px',
              padding: '10px',
              background: '#fff2f0',
              border: '1px solid #ffccc7',
              borderRadius: '4px',
              color: '#ff4d4f',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <AlertCircle size={16} />
              <span>{errors.items}</span>
            </div>
          )}

          {items.map((item, index) => {
            const originalItem = item.original_item;
            const originalQty = originalItem ? originalItem.quantity : 0;
            const newQty = parseFloat(item.quantity) || 0;
            const qtyChanged = originalQty !== newQty;

            return (
              <div key={index} style={{
                marginBottom: '20px',
                padding: '20px',
                background: '#fafafa',
                borderRadius: '8px',
                border: qtyChanged ? '2px solid #1890ff' : '1px solid #f0f0f0',
                position: 'relative'
              }}>
                {item.delivered_quantity > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    padding: '4px 8px',
                    background: '#e6f7ff',
                    border: '1px solid #91d5ff',
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: '#1890ff'
                  }}>
                    Delivered: {item.delivered_quantity} / {item.quantity}
                  </div>
                )}

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "3fr 1fr 1fr 1fr",
                    gap: "10px",
                  }}
                >
                  {/* Product Selection */}
                  <div>
                    <label style={{ fontSize: 12, color: '#666' }}>Product</label>
                    <Select
                      value={
                        item.product_id
                          ?
                          {
                            value: item.product_id,
                            label:
                              (() => {
                                const p =
                                  filteredProducts.find(
                                    x => x._id === item.product_id
                                  );
                                if (!p) return "";
                                return `${p.product_name} (${p.product_code}) | Stock: ${productStock[p._id]?.available_stock || 0
                                  } | Price: ${formatCurrency(
                                    productPrices[p._id] ||
                                    p.sale_price ||
                                    0
                                  )}`;
                              })()
                          }
                          :
                          null
                      }

                      options={getProductOptions(index)}

                      isSearchable={true}

                      placeholder={
                        !formData.warehouse_id
                          ?
                          "Select warehouse first"
                          :
                          "Search product..."
                      }

                      isDisabled={
                        !formData.warehouse_id ||
                        loadingProducts ||
                        item.delivered_quantity > 0 ||
                        orderStatus === "delivered" ||
                        orderStatus === "cancelled"
                      }

                      onChange={(selected) => {
                        if (!selected) {
                          handleProductSelect(index, "");
                          return;
                        }

                        const duplicate = items.some(
                          (row, rowIndex) =>
                            rowIndex !== index &&
                            String(row.product_id) ===
                            String(selected.value)
                        );

                        if (duplicate) {
                          toast.warning(
                            "This product is already added"
                          );
                          return;
                        }

                        handleProductSelect(
                          index,
                          selected.value
                        );
                      }}

                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: "38px",
                          border: "1px solid #ddd",
                          borderRadius: "4px"
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 9999
                        })
                      }}
                    />
                    {item.product_id && productStock[item.product_id] && (
                      <div style={{ fontSize: 11, marginTop: 4, color: '#52c41a' }}>
                        <Layers size={12} style={{ display: 'inline', marginRight: 4 }} />
                        Available: {productStock[item.product_id].available_stock} |
                        Reserved: {productStock[item.product_id].reserved_stock}
                      </div>
                    )}
                    {qtyChanged && (
                      <div style={{ fontSize: 11, marginTop: 4, color: '#1890ff' }}>
                        <RefreshCw size={12} style={{ display: 'inline', marginRight: 4 }} />
                        Changed from {originalQty} to {newQty}
                      </div>
                    )}
                    {errors[`stock_${index}`] && (
                      <div style={{ fontSize: 11, marginTop: 4, color: '#ff4d4f' }}>
                        <AlertCircle size={12} style={{ display: 'inline', marginRight: 4 }} />
                        {errors[`stock_${index}`]}
                      </div>
                    )}
                  </div>

                  {/* Quantity */}
                  <div>
                    <label style={{ fontSize: 12, color: '#666' }}>Quantity</label>
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                      placeholder="0"
                      required
                      min="1"
                      disabled={item.delivered_quantity > 0 || orderStatus === 'delivered' || orderStatus === 'cancelled'}
                      style={{
                        width: '100%',
                        padding: 8,
                        borderRadius: 4,
                        border: '1px solid #ddd',
                        background: item.delivered_quantity > 0 ? '#f5f5f5' : 'white'
                      }}
                    />
                    {item.delivered_quantity > 0 && (
                      <div style={{ fontSize: 11, marginTop: 4, color: '#ff4d4f' }}>
                        Cannot change quantity after partial receipt
                      </div>
                    )}
                  </div>

                  {/* Unit Price */}
                  <div>
                    <label style={{ fontSize: 12, color: '#666' }}>Unit Price</label>
                    <input
                      type="number"
                      value={item.unit_price}
                      onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                      placeholder="0.00"
                      required
                      min="0"
                      step="0.01"
                      disabled={item.delivered_quantity > 0 || orderStatus === 'delivered' || orderStatus === 'cancelled'}
                      style={{
                        width: '100%',
                        padding: 8,
                        borderRadius: 4,
                        border: '1px solid #ddd',
                        background: item.delivered_quantity > 0 ? '#f5f5f5' : 'white'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: 12, color: "#666" }}>Gross Total</label>
                    <input
                      type="text"
                      value={formatCurrency(
                        (Number(item.quantity) || 0) *
                        (Number(item.unit_price) || 0)
                      )}
                      readOnly
                      style={{
                        width: "100%",
                        padding: 8,
                        borderRadius: 4,
                        border: "1px solid #ddd",
                        background: "#f5f5f5"
                      }}
                    />
                  </div>
                </div>

                {/* Item Notes */}
                <div style={{ marginTop: '10px' }}>
                  <input
                    type="text"
                    placeholder="Item notes (optional)"
                    value={item.notes}
                    onChange={(e) => handleItemChange(index, 'notes', e.target.value)}
                    disabled={item.delivered_quantity > 0 || orderStatus === 'delivered' || orderStatus === 'cancelled'}
                    style={{
                      width: '100%',
                      padding: 8,
                      borderRadius: 4,
                      border: '1px solid #ddd',
                      background: item.delivered_quantity > 0 ? '#f5f5f5' : 'white'
                    }}
                  />
                </div>

                {/* Item Total */}
                <div style={{
                  marginTop: '10px',
                  padding: '8px 12px',
                  background: '#f0f0f0',
                  borderRadius: '4px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{ display: "flex", gap: 18 }}>
                    <span>
                      Discount:{" "}
                      <strong style={{ color: "#e53935" }}>
                        -{formatCurrency(
                          calculateItem(item, index).discountAmount
                        )}
                      </strong>
                    </span>
                    <span>
                      Tax:{" "}
                      <strong>
                        +{formatCurrency(
                          calculateItem(item, index).taxAmount
                        )}
                      </strong>
                    </span>
                  </div>
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#1890ff"
                    }}
                  >
                    Net Total:{" "}
                    {formatCurrency(
                      calculateItemTotal(item, index)
                    )}
                  </span>
                </div>

                {/* Remove button for additional rows */}
                {index > 0 && item.delivered_quantity === 0 && (
                  <button
                    type="button"
                    onClick={() => removeItemRow(index)}
                    style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      padding: '4px 8px',
                      background: '#fff1f0',
                      border: '1px solid #ffccc7',
                      borderRadius: '4px',
                      color: '#ff4d4f',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4
                    }}
                    title="Remove Item"
                  >
                    <Trash2 size={14} /> Remove
                  </button>
                )}
              </div>
            );
          })}

          <div style={{ marginBottom: '30px' }}>
            <button
              type="button"
              onClick={addItemRow}
              style={{
                padding: '10px 20px',
                background: 'transparent',
                border: '2px dashed #667eea',
                borderRadius: '6px',
                color: '#667eea',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: 600
              }}
              disabled={!formData.warehouse_id || orderStatus === 'delivered' || orderStatus === 'cancelled'}
            >
              <Plus size={16} /> Add Another Item
            </button>
            {!formData.warehouse_id && (
              <small style={{ display: 'block', marginTop: '5px', color: '#fa8c16' }}>
                Please select a warehouse first to add items
              </small>
            )}
          </div>

          <h3 className="section-title">
            Order Discount and Tax
          </h3>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 20,
              marginBottom: 20,
              padding: 16,
              background: "#fafafa",
              border: "1px solid #f0f0f0",
              borderRadius: 8,
            }}
          >
            <div>
              <label style={{ display: "block", marginBottom: 5, fontWeight: 500 }}>
                Apply Discount to All Items
              </label>
              <select
                name="discount_id"
                value={formData.discount_id}
                onChange={handleChange}
                disabled={
                  orderStatus === "delivered" ||
                  orderStatus === "cancelled"
                }
                style={{
                  width: "100%",
                  padding: 8,
                  borderRadius: 4,
                  border: "1px solid #ddd",
                }}
              >
                <option value="">No Discount</option>
                {discounts.map((discount) => (
                  <option key={discount._id} value={discount._id}>
                    {discount.name} ({discount.code}) -{" "}
                    {discount.discount_type === "percentage"
                      ? `${discount.value}%`
                      : formatCurrency(discount.value)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: "block", marginBottom: 5, fontWeight: 500 }}>
                Apply Tax to All Items
              </label>
              <select
                name="tax_id"
                value={formData.tax_id}
                onChange={handleChange}
                disabled={
                  orderStatus === "delivered" ||
                  orderStatus === "cancelled"
                }
                style={{
                  width: "100%",
                  padding: 8,
                  borderRadius: 4,
                  border: "1px solid #ddd",
                }}
              >
                <option value="">No Tax</option>
                {taxes.map((tax) => (
                  <option key={tax._id} value={tax._id}>
                    {tax.name} ({tax.code}) -{" "}
                    {tax.tax_type === "percentage"
                      ? `${tax.value}%`
                      : formatCurrency(tax.value)}
                    {" · "}
                    {tax.price_inclusive ? "Inclusive" : "Exclusive"}
                    {tax.recoverable ? " · Recoverable" : ""}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: "block", marginBottom: 5, fontWeight: 500 }}>
                Manual Discount Amount
              </label>
              <input
                type="text"
                inputMode="decimal"
                name="manual_discount"
                value={formData.manual_discount}
                onChange={handleChange}
                placeholder="Example: 5.00"
                disabled={
                  orderStatus === "delivered" ||
                  orderStatus === "cancelled"
                }
                style={{
                  width: "100%",
                  padding: 8,
                  borderRadius: 4,
                  border: errors.manual_discount
                    ? "1px solid #ff4d4f"
                    : "1px solid #ddd",
                }}
              />
              {errors.manual_discount && (
                <small style={{ color: "#ff4d4f" }}>
                  {errors.manual_discount}
                </small>
              )}
            </div>
          </div>

          {/* Posted Sales-Order Payments */}
          {postedPayments.length > 0 && (
            <>
              <h3 className="section-title">
                Posted Sales-Order Payments
              </h3>

              <div
                style={{
                  background: "#f0f9ff",
                  border: "1px solid #91d5ff",
                  borderRadius: 8,
                  padding: 16,
                  marginBottom: 20,
                }}
              >
                <div
                  style={{
                    marginBottom: 12,
                    color: "#0958d9",
                    fontSize: 13,
                  }}
                >
                  These payments are already posted to the ledger and
                  cannot be edited from the sales-order form. Cancel
                  the order to reverse them, then create a replacement
                  order if the payment amounts are wrong.
                </div>

                {postedPayments.map((payment, paymentIndex) => {
                  const matchingAccount =
                    paymentMethodsData.methods
                      .flatMap((method) => method.accounts || [])
                      .find(
                        (account) =>
                          String(account._id) ===
                          String(payment.account_id),
                      );

                  return (
                    <div
                      key={`${payment.account_id}-${paymentIndex}`}
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "1fr 2fr 1fr 1.5fr",
                        gap: 12,
                        padding: "10px 12px",
                        background: "white",
                        border: "1px solid #d9d9d9",
                        borderRadius: 6,
                        marginBottom: 8,
                      }}
                    >
                      <strong style={{ textTransform: "capitalize" }}>
                        {String(payment.method || "").replaceAll(
                          "_",
                          " ",
                        )}
                      </strong>

                      <span>
                        {payment.account_name ||
                          matchingAccount?.account_name ||
                          "Account not stored"}
                      </span>

                      <strong style={{ color: "#52c41a" }}>
                        {formatCurrency(payment.amount)}
                      </strong>

                      <span>{payment.reference || "-"}</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Additional Charges */}
          <h3 className="section-title">Additional Charges</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Shipping Cost</label>
              <input
                type="number"
                name="shipping_cost"
                value={formData.shipping_cost}
                onChange={handleChange}
                placeholder="0.00"
                min="0"
                step="0.01"
                disabled={orderStatus === 'delivered' || orderStatus === 'cancelled'}
                style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Other Charges</label>
              <input
                type="number"
                name="other_charges"
                value={formData.other_charges}
                onChange={handleChange}
                placeholder="0.00"
                min="0"
                step="0.01"
                disabled={orderStatus === 'delivered' || orderStatus === 'cancelled'}
                style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
              />
            </div>
          </div>

          {/* Approval Checkbox */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="requires_approval"
                checked={formData.requires_approval}
                onChange={handleChange}
                disabled={orderStatus === 'delivered' || orderStatus === 'cancelled'}
              />
              <span>This order requires approval</span>
            </label>
          </div>

          {/* Totals Summary */}
          <h3 className="section-title">Totals Summary</h3>
          <div
            style={{
              background: "#fafafa",
              padding: 20,
              borderRadius: 8,
              border: "1px solid #f0f0f0",
              marginBottom: 20,
            }}
          >
            {[
              ["Subtotal", totals.subTotal, ""],
              ["Selected Discount", totals.selectedDiscountAmount, "-"],
              ["Manual Discount", totals.manualDiscountAmount, "-"],
              ["Total Discount", totals.totalDiscount, "-"],
              ["Selected Tax", totals.selectedTaxAmount, "+"],
              ["Total Tax", totals.totalTax, "+"],
              ["Shipping", totals.shippingCost, "+"],
              ["Other Charges", totals.otherCharges, "+"],
            ].map(([label, value, prefix]) => (
              <div
                key={label}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 10,
                }}
              >
                <span>{label}:</span>
                <strong
                  style={
                    prefix === "-"
                      ? { color: "#e53935" }
                      : undefined
                  }
                >
                  {prefix}
                  {formatCurrency(value)}
                </strong>
              </div>
            ))}

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 15,
                paddingTop: 15,
                borderTop: "2px solid #e0e0e0",
                fontSize: 18,
                fontWeight: "bold",
              }}
            >
              <span>Grand Total:</span>
              <strong style={{ color: "#1890ff" }}>
                {formatCurrency(totals.grandTotal)}
              </strong>
            </div>

            {Number(salesOrder.total_amount_paid || 0) > 0 && (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: 12,
                    paddingTop: 12,
                    borderTop: "1px dashed #d9d9d9",
                  }}
                >
                  <span>Posted Payments:</span>
                  <strong style={{ color: "#52c41a" }}>
                    {formatCurrency(
                      Number(salesOrder.total_amount_paid || 0),
                    )}
                  </strong>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: 10,
                  }}
                >
                  <span>Balance:</span>
                  <strong
                    style={{
                      color:
                        totals.grandTotal -
                          Number(salesOrder.total_amount_paid || 0) >
                          0.009
                          ? "#ff4d4f"
                          : "#52c41a",
                    }}
                  >
                    {formatCurrency(
                      Math.max(
                        totals.grandTotal -
                        Number(salesOrder.total_amount_paid || 0),
                        0,
                      ),
                    )}
                  </strong>
                </div>
              </>
            )}
          </div>

          {/* Template Download Button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
            <button
              type="button"
              onClick={downloadTemplate}
              style={{
                padding: '8px 16px',
                background: '#52c41a',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px'
              }}
            >
              <Download size={16} /> Download Template
            </button>
          </div>

          {/* Convert to Invoice Button */}
          {orderStatus !== 'draft' && orderStatus !== 'delivered' && orderStatus !== 'cancelled' && deliveryStats.pendingItems > 0 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
              <button
                type="button"
                onClick={handleConvertToInvoice}
                disabled={submitting}
                style={{
                  padding: '8px 16px',
                  background: '#722ed1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px'
                }}
              >
                <FilePlus size={16} /> Convert to Invoice
              </button>
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '15px', marginTop: '30px' }}>
            <button
              type="button"
              onClick={handleCancel}
              disabled={submitting}
              style={{
                padding: '12px 24px',
                background: 'white',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                color: '#666',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px'
              }}
            >
              <X size={16} /> Cancel
            </button>

            <button
              type="submit"
              disabled={submitting || orderStatus === 'delivered' || orderStatus === 'cancelled'}
              style={{
                padding: '12px 24px',
                background: orderStatus === 'delivered' || orderStatus === 'cancelled' ? '#d9d9d9' : '#1890ff',
                border: 'none',
                borderRadius: '6px',
                color: 'white',
                cursor: orderStatus === 'delivered' || orderStatus === 'cancelled' ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: 600,
                opacity: submitting ? 0.7 : 1
              }}
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  Updating...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Update Order
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditSalesOrder;